import React from 'react';
import useAnalyticsTracking from '../hooks/useAnalyticsTracking';

// Higher-order component to track interactions
export const withAnalyticsTracking = (WrappedComponent, trackingOptions = {}) => {
    return React.forwardRef((props, ref) => {
        const { trackInteraction } = useAnalyticsTracking();

        const handleClick = (event) => {
            // Track the click interaction
            const element = event.target;
            const elementId = element.id || element.className || 'unknown';
            const elementText = element.textContent || element.alt || element.title || '';
            
            trackInteraction('button_click', elementId, elementText, {
                ...trackingOptions,
                elementType: element.tagName.toLowerCase(),
                timestamp: new Date().toISOString()
            });

            // Call original onClick if it exists
            if (props.onClick) {
                props.onClick(event);
            }
        };

        return (
            <WrappedComponent
                {...props}
                ref={ref}
                onClick={handleClick}
            />
        );
    });
};

// Specific components for common UI elements
export const AnalyticsButton = withAnalyticsTracking('button', { elementType: 'button' });
export const AnalyticsLink = withAnalyticsTracking('a', { elementType: 'link' });
export const AnalyticsDiv = withAnalyticsTracking('div', { elementType: 'div' });

// Generic component that can track any element
export const AnalyticsTracker = ({ 
    children, 
    trackingData = {}, 
    interactionType = 'click',
    as: Component = 'div',
    ...props 
}) => {
    const { trackInteraction } = useAnalyticsTracking();

    const handleInteraction = (event) => {
        const element = event.target;
        const elementId = element.id || element.className || 'unknown';
        const elementText = element.textContent || element.alt || element.title || '';
        
        trackInteraction(
            interactionType === 'click' ? 'button_click' : interactionType,
            elementId,
            elementText,
            {
                ...trackingData,
                elementType: element.tagName?.toLowerCase(),
                timestamp: new Date().toISOString()
            }
        );

        // Call original handler if it exists
        const eventHandler = interactionType === 'click' ? props.onClick : 
                            interactionType === 'hover' ? props.onMouseEnter :
                            interactionType === 'focus' ? props.onFocus : null;
        
        if (eventHandler) {
            eventHandler(event);
        }
    };

    const eventProps = {
        [interactionType === 'click' ? 'onClick' : 
         interactionType === 'hover' ? 'onMouseEnter' :
         interactionType === 'focus' ? 'onFocus' : 'onClick']: handleInteraction
    };

    return (
        <Component {...props} {...eventProps}>
            {children}
        </Component>
    );
};

// Hook to manually track custom events
export const useAnalyticsEvent = () => {
    const { trackInteraction } = useAnalyticsTracking();

    const trackEvent = (eventType, eventData = {}) => {
        trackInteraction('custom_event', eventType, JSON.stringify(eventData), {
            eventType,
            ...eventData,
            timestamp: new Date().toISOString()
        });
    };

    const trackFormSubmit = (formName, formData = {}) => {
        trackInteraction('form_submit', formName, '', {
            formName,
            formFields: Object.keys(formData),
            timestamp: new Date().toISOString()
        });
    };

    const trackDownload = (fileName, fileType = '') => {
        trackInteraction('download', fileName, fileType, {
            fileName,
            fileType,
            timestamp: new Date().toISOString()
        });
    };

    const trackUpload = (fileName, fileType = '', fileSize = 0) => {
        trackInteraction('upload', fileName, fileType, {
            fileName,
            fileType,
            fileSize,
            timestamp: new Date().toISOString()
        });
    };

    const trackSearch = (searchQuery, resultCount = 0) => {
        trackInteraction('search', 'search_query', searchQuery, {
            query: searchQuery,
            resultCount,
            timestamp: new Date().toISOString()
        });
    };

    const trackNavigation = (fromPage, toPage) => {
        trackInteraction('navigation', 'page_change', '', {
            fromPage,
            toPage,
            timestamp: new Date().toISOString()
        });
    };

    return {
        trackEvent,
        trackFormSubmit,
        trackDownload,
        trackUpload,
        trackSearch,
        trackNavigation
    };
};

export default AnalyticsTracker; 